/**
 * React hooks for applying SEO metadata to the DOM.
 *
 * Thin wrappers — these hooks set document.title, meta tags, canonical links,
 * and JSON-LD structured data. The actual data can come from payloads.ts
 * (for prerenderable pages) or be passed directly (for auth-gated pages).
 */

import { useEffect } from 'react';

// ---------------------------------------------------------------------------
// useSEO
// ---------------------------------------------------------------------------

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
}

/**
 * Set document title, meta description, canonical URL, OG tags, and robots.
 * Updates existing tags or creates new ones. No cleanup on unmount — each
 * page overrides the previous values.
 */
export function useSEO({
  title = 'Bar Exam & Law School Prep | JD Simplified',
  description = 'Practice-first law school and bar prep with case briefs, outlines, flashcards, and feedback — your progress carries forward from 1L to the bar.',
  image = '/images/og-jds-default.png',
  url = 'https://jdsimplified.com',
  type = 'website',
  noindex = false,
}: SEOProps = {}) {
  useEffect(() => {
    document.title = title;

    // Resolve a root-relative `image` against the ORIGIN of `url`, not against
    // the whole page URL.
    //
    // This used to be `${url}${image}`, which is only correct when `url` is a
    // bare origin — the parameter default. Of 23 production call sites, only 2
    // pass `image` at all; 18 pass a real page url WITHOUT one, and every one of
    // those got page-url + root-relative-path — e.g.
    // '.../bar/practice/trial/images/og-jds-default.png', a 404. Nothing
    // errored: og:image is never fetched by the page, so a broken card only
    // shows up when an external scraper fetches it. Prerendered HTML was unaffected
    // (payloads.ts builds absolute image URLs), so the bad value appeared only
    // AFTER hydration overwrote the good one — invisible to a view-source check.
    //
    // Absolute images pass through untouched.
    const absoluteImage = (() => {
      if (/^https?:\/\//i.test(image)) return image;
      try {
        return new URL(image, url).href;
      } catch {
        return `https://jdsimplified.com${image.startsWith('/') ? '' : '/'}${image}`;
      }
    })();

    const updateMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${property}"]`);

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }

      element.setAttribute('content', content);
    };

    // Standard meta tags
    updateMetaTag('description', description);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', absoluteImage, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', 'JD Simplified', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', absoluteImage);

    // Additional SEO tags
    updateMetaTag('author', 'JD Simplified, LLC');
    updateMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    return () => {
      // No cleanup — next page overrides
    };
  }, [title, description, image, url, type, noindex]);
}

// ---------------------------------------------------------------------------
// useStructuredData
// ---------------------------------------------------------------------------

/**
 * Inject page-specific JSON-LD structured data into <head>.
 * Manages a single script tag per id — updates on re-render, removes on unmount.
 */
export function useStructuredData(id: string, schema: Record<string, unknown> | null) {
  useEffect(() => {
    if (!schema) return;

    let script = document.getElementById(id) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    return () => {
      script?.remove();
    };
  }, [id, schema]);
}

// ---------------------------------------------------------------------------
// Structured data helpers (used by `/` and other pages).
// Said "HomePage" until the 2026-09-11 cutover; that component is deleted and
// `/` renders src/pages/marketing/HomeVariantBPage.tsx.
// ---------------------------------------------------------------------------

export {
  getOrganizationSchema,
  getWebsiteSchema,
  getSoftwareApplicationSchema,
  getFAQSchema,
} from './schema-helpers';
